import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import GlassCard from '@/components/ui/glass-card';
import { Input } from '@/components/ui/input';
import {
  cardHover,
  fadeIn,
  glassCard,
  iconHover,
  pageTransition,
  slideUp,
  staggerContainer,
  textReveal,
} from '@/lib/animations';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowUp, ChevronDown, Mic, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

const AiMetric = () => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(3);
  const [selectedTrend, setSelectedTrend] = useState('Collections');
  const [isCustomRangeActive, setIsCustomRangeActive] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(''); // YYYY-MM
  const [customEndDate, setCustomEndDate] = useState(''); // YYYY-MM
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content:
        "Hello! I'm here to help you analyze your practice metrics. What would you like to know?",
      time: '2 min ago',
    },
    {
      type: 'user',
      content: "Show me today's performance summary",
      time: '1 min ago',
    },
    {
      type: 'ai',
      content:
        "Based on today's data, your practice is performing well with 92% booking rate and $390 average collection per exam.",
      time: 'Just now',
    },
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const monthlyGoals = [
    { label: 'Month to date', progress: 60 },
    { label: 'Exams', progress: 40 },
    { label: 'ADT', progress: 30 },
    { label: 'New Patients', progress: 25 },
    { label: 'Established Patients', progress: 15 },
    { label: 'Other', progress: 35 },
  ];

  const metricsData = [
    {
      label: 'Total Collection per Exam',
      value: '$390',
      trend: 'up',
      color: 'text-green-500',
    },
    {
      label: 'Premium Frames',
      value: '38%',
      trend: 'down',
      color: 'text-red-500',
    },
    {
      label: 'Optical Capture',
      value: '70%',
      trend: 'down',
      color: 'text-red-500',
    },
    { label: 'No Shows', value: '3%', trend: 'up', color: 'text-green-500' },
    { label: 'Booked', value: '92%', trend: 'up', color: 'text-green-500' },
    {
      label: 'Lens Upgrade',
      value: '80%',
      trend: 'up',
      color: 'text-green-500',
    },
    {
      label: 'Contact Lens Sales',
      value: '$245',
      trend: 'up',
      color: 'text-green-500',
    },
    {
      label: 'Frame Sales',
      value: '$1,250',
      trend: 'down',
      color: 'text-red-500',
    },
    {
      label: 'Insurance Claims',
      value: '95%',
      trend: 'up',
      color: 'text-green-500',
    },
    {
      label: 'Patient Satisfaction',
      value: '4.8',
      trend: 'up',
      color: 'text-green-500',
    },
    {
      label: 'Average Visit Time',
      value: '45min',
      trend: 'down',
      color: 'text-red-500',
    },
    {
      label: 'Referral Rate',
      value: '12%',
      trend: 'up',
      color: 'text-green-500',
    },
    // {
    //   label: 'Revenue per Hour',
    //   value: '$485',
    //   trend: 'up',
    //   color: 'text-green-500',
    // },
    // {
    //   label: 'Conversion Rate',
    //   value: '75%',
    //   trend: 'down',
    //   color: 'text-red-500',
    // },
    // {
    //   label: 'Follow-up Rate',
    //   value: '88%',
    //   trend: 'up',
    //   color: 'text-green-500',
    // },
    // {
    //   label: 'Lab Turnaround',
    //   value: '5 days',
    //   trend: 'down',
    //   color: 'text-red-500',
    // },
  ];

  const getChartData = () => {
    const allData = [
      { month: 'Jul 24', Collections: 250, Sales: 150 },
      { month: 'Aug 24', Collections: 260, Sales: 160 },
      { month: 'Sep 24', Collections: 230, Sales: 140 },
      { month: 'Oct 24', Collections: 280, Sales: 170 },
      { month: 'Nov 24', Collections: 300, Sales: 190 },
      { month: 'Dec 24', Collections: 450, Sales: 250 },
      { month: 'Jan 25', Collections: 275, Sales: 185 },
      { month: 'Feb 25', Collections: 275, Sales: 195 },
      { month: 'Mar 25', Collections: 797, Sales: 420 },
      { month: 'Apr 25', Collections: 650, Sales: 380 },
      { month: 'May 25', Collections: 720, Sales: 450 },
      { month: 'Jun 25', Collections: 580, Sales: 320 },
    ];

    return allData.slice(-selectedMonths);
  };

  const chartConfig = {
    [selectedTrend]: {
      label: selectedTrend,
      color: 'hsl(142, 76%, 36%)',
    },
  };

  const blueColor = 'hsl(221, 83%, 53%)';
  const greenColor = chartConfig[selectedTrend].color;
  const referenceValue = 500;

  const suggestions = [
    'How is my practice doing?',
    'What was the highest most recent optical sale today?',
    'Were there any anomalies lately?',
    'Are we on target to achieve our monthly goals?',
    'Show me patient satisfaction trends',
    "What's our conversion rate this month?",
  ];

  const handleSendMessage = () => {
    if (message.trim()) {
      setMessages([
        ...messages,
        { type: 'user', content: message, time: 'now' },
      ]);
      setMessage('');
      // Simulate Ai response
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            type: 'ai',
            content:
              'Thank you for your question. Let me analyze the data and provide you with insights.',
            time: 'now',
          },
        ]);
      }, 1000);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setMessages([
      ...messages,
      { type: 'user', content: suggestion, time: 'now' },
    ]);
    // Simulate Ai response
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          type: 'ai',
          content:
            'Let me analyze that for you and provide detailed insights based on your current data.',
          time: 'now',
        },
      ]);
    }, 1000);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Layout>
      <motion.div className="w-full" {...pageTransition}>
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-3 md:gap-3">
          {/* Left Column - Ai Assistant Chat */}
          <motion.div
            className="lg:col-span-3"
            variants={glassCard}
            initial="initial"
            animate="animate"
          >
            <GlassCard className="h-full min-h-[calc(100vh-9.5rem)]">
              <div className="p-4 md:p-6 flex flex-col h-full">
                {/* Chat Header */}
                <motion.div className="mb-6" variants={fadeIn}>
                  <motion.h2
                    className="text-xl font-semibold text-foreground mb-4"
                    variants={textReveal}
                  >
                    Ai Assistant
                  </motion.h2>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={iconHover}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsVoiceMode(false)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        !isVoiceMode
                          ? 'bg-blue-500/90 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
                      }`}
                    >
                      Text Mode
                    </motion.button>
                    <motion.button
                      whileHover={iconHover}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsVoiceMode(true)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        isVoiceMode
                          ? 'bg-blue- 500/90 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
                      }`}
                    >
                      Voice Mode
                    </motion.button>
                  </div>
                </motion.div>

                {/* Chat Messages */}
                <motion.div
                  ref={scrollRef}
                  className="flex-1 min-h-[calc(100vh-22.5rem)] overflow-y-auto mb-4 space-y-4 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent"
                  variants={staggerContainer}
                  initial="initial"
                  animate="animate"
                >
                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      variants={slideUp}
                      className={`flex ${
                        msg.type === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <motion.div
                        whileHover={cardHover}
                        className={`max-w-[85%] ${
                          msg.type === 'user'
                            ? 'bg-gradient-to-r from-blue-500/90 to-indigo-500/90 text-white rounded-2xl rounded-br-md shadow-lg shadow-blue-500/20'
                            : 'bg-muted/100 backdrop-blur-sm text-foreground rounded-2xl rounded-bl-md shadow-lg shadow-black/5 dark:shadow-white/5'
                        } p-3`}
                      >
                        <motion.p
                          className="text-sm break-words"
                          variants={textReveal}
                        >
                          {msg.content}
                        </motion.p>
                        <motion.p
                          className={`text-xs mt-1 ${
                            msg.type === 'user'
                              ? 'text-blue-100'
                              : 'text-muted-foreground'
                          }`}
                          variants={textReveal}
                        >
                          {msg.time}
                        </motion.p>
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Input Area */}
                <motion.div
                  className="flex items-center space-x-2 mt-auto"
                  variants={fadeIn}
                >
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder={
                      isVoiceMode ? "I'm listening..." : 'Type your message...'
                    }
                    className="glass-button text-foreground placeholder-muted-foreground bg-muted/50 backdrop-blur-sm shadow-lg shadow-black/5 dark:shadow-white/5"
                  />
                  {isVoiceMode ? (
                    <motion.div whileHover={iconHover}>
                      <Button
                        onClick={() => setIsRecording(!isRecording)}
                        className={`${
                          isRecording
                            ? 'bg-red-500/90 hover:bg-red-600/90 shadow-lg shadow-red-500/20'
                            : 'glass-button bg-muted/50 backdrop-blur-sm shadow-lg shadow-black/5 dark:shadow-white/5'
                        } text-foreground`}
                      >
                        <Mic className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div whileHover={iconHover}>
                      <Button
                        onClick={handleSendMessage}
                        className="glass-button bg-muted/50 backdrop-blur-sm shadow-lg shadow-black/5 dark:shadow-white/5 text-foreground"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Right Column - All Content */}
          <motion.div
            className="lg:col-span-7 flex flex-col space-y-4 md:space-y-4"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {/* Top Row - Monthly Goals and Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-3">
              {/* Monthly Goals */}
              <motion.div variants={glassCard} className="h-full">
                <GlassCard className="h-[calc(50vh-4.9rem)]">
                  <div className="p-4 md:p-6">
                    <motion.h2
                      className="text-lg font-semibold text-foreground mb-4"
                      variants={textReveal}
                    >
                      Monthly Goals
                    </motion.h2>
                    <motion.div
                      className="grid grid-cols-1 gap-3"
                      variants={staggerContainer}
                      initial="initial"
                      animate="animate"
                    >
                      {monthlyGoals.map((goal, index) => (
                        <motion.div key={index} variants={slideUp}>
                          <div className="flex justify-between items-center mb-2">
                            <motion.span
                              className="text-foreground text-sm"
                              variants={textReveal}
                            >
                              {goal.label}
                            </motion.span>
                            <motion.span
                              className="text-foreground text-sm font-medium"
                              variants={textReveal}
                            >
                              {goal.progress}%
                            </motion.span>
                          </div>
                          <div className="w-full bg-muted/30 rounded-full h-2">
                            <motion.div
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${goal.progress}%` }}
                              transition={{ duration: 0.5, ease: 'easeOut' }}
                            />
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>
                </GlassCard>
              </motion.div>

              {/* Trends Chart */}
              <motion.div variants={glassCard}>
                <GlassCard className="h-[calc(50vh-4.9rem)]">
                  <div className="p-4 md:p-6 flex flex-col h-full">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2 md:gap-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <motion.div whileHover={iconHover}>
                            <Button
                              variant="ghost"
                              className="text-lg font-semibold text-foreground p-0 h-auto"
                            >
                              <motion.span variants={textReveal}>
                                Trends – {selectedTrend}
                              </motion.span>
                              <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </motion.div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-background border border-border">
                          <DropdownMenuItem
                            onClick={() => setSelectedTrend('Collections')}
                          >
                            Trends – Collections
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setSelectedTrend('Sales')}
                          >
                            Trends – Sales
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div className="flex flex-wrap gap-1">
                        {[3, 6, 9, 12].map((months) => (
                          <motion.div
                            key={months}
                            whileHover={iconHover}
                            whileTap={{ scale: 0.95 }}
                          >
                            <button
                              onClick={() => setSelectedMonths(months)}
                              className={`px-2 md:px-3 py-1 text-xs rounded-lg transition-colors ${
                                selectedMonths === months
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
                              }`}
                            >
                              {months}M
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <motion.div
                      className="flex-1 w-full h-full min-h-0"
                      variants={fadeIn}
                    >
                      <ChartContainer
                        config={chartConfig}
                        className="w-full h-full"
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={getChartData()}
                            margin={{
                              top: 10,
                              right: 10,
                              left: 10,
                              bottom: 10,
                            }}
                          >
                            <XAxis
                              dataKey="month"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: 'var(--foreground)', fontSize: 11 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: 'var(--foreground)', fontSize: 11 }}
                              tickFormatter={(value) => `$${value}`}
                              width={40}
                            />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <ReferenceLine
                              y={referenceValue}
                              stroke="hsl(var(--muted-foreground))"
                              strokeDasharray="3 3"
                            />
                            <Bar dataKey={selectedTrend} radius={[3, 3, 0, 0]}>
                              {getChartData().map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    entry[selectedTrend] >= referenceValue
                                      ? greenColor
                                      : blueColor
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </motion.div>
                  </div>
                </GlassCard>
              </motion.div>
            </div>

            {/* Metrics Grid */}
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-3"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              {metricsData.map((metric, index) => (
                <motion.div
                  key={index}
                  variants={glassCard}
                  whileHover={cardHover}
                >
                  <GlassCard>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <motion.p
                            className="text-muted-foreground text-xs mb-1 truncate"
                            variants={textReveal}
                          >
                            {metric.label}
                          </motion.p>
                          <motion.p
                            className="text-lg font-bold text-foreground"
                            variants={textReveal}
                          >
                            {metric.value}
                          </motion.p>
                        </div>
                        <motion.div
                          whileHover={iconHover}
                          className={`${metric.color} p-1.5 rounded-full`}
                        >
                          {metric.trend === 'up' ? (
                            <ArrowUp className="w-6 h-6" />
                          ) : (
                            <ArrowDown className="w-6 h-6" />
                          )}
                        </motion.div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </motion.div>

            {/* Suggested Questions */}
            <motion.div
              className="mt-3"
              variants={fadeIn}
              initial="initial"
              animate="animate"
            >
              <motion.div variants={glassCard}>
                <GlassCard>
                  <div className="p-4">
                    <motion.p
                      className="text-muted-foreground text-sm font-semibold mb-3"
                      variants={textReveal}
                    >
                      Suggested Questions
                    </motion.p>
                    <div
                      ref={suggestionsRef}
                      className="group flex gap-2 md:gap-3 overflow-x-auto pb-2"
                      style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      <div className="flex gap-2 md:gap-3 animate-scroll-slow group-hover:[animation-play-state:paused]">
                        {[...suggestions, ...suggestions].map(
                          (suggestion, index) => (
                            <motion.div key={index} whileTap={{ scale: 0.95 }}>
                              <button
                                onClick={() =>
                                  handleSuggestionClick(suggestion)
                                }
                                className="px-3 md:px-4 py-2 bg-accent/100 hover:bg-slate-300/90 hover:dark:bg-slate-900 rounded-2xl text-sm text-foreground hover:text-foreground transition-colors whitespace-nowrap flex-shrink-0"
                              >
                                {suggestion}
                              </button>
                            </motion.div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
};

export default AiMetric;
